import { useState, useEffect, useCallback } from "react";
import { api, formatApiError, BACKEND, formatPrice } from "@/lib/api";
import { toast } from "sonner";
import { COLLECTIONS, EMPTY } from "./constants";
import CollectionOrder from "./CollectionOrder";

export default function ProductManager() {
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const images = form.imagesText.split("\n").map((s) => s.trim()).filter(Boolean);
  const sizeList = form.sizesText.split(",").map((s) => s.trim()).filter(Boolean);

  const reorderImages = (from, to) => {
    if (from === null || to === null || from === to) return;
    setForm((prev) => {
      const arr = prev.imagesText.split("\n").map((s) => s.trim()).filter(Boolean);
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return { ...prev, imagesText: arr.join("\n") };
    });
  };

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/uploads", fd);
        urls.push(`${BACKEND}${data.url}`);
      }
      setForm((prev) => {
        const existing = prev.imagesText.split("\n").map((s) => s.trim()).filter(Boolean);
        return { ...prev, imagesText: [...existing, ...urls].join("\n") };
      });
      toast.success(`${urls.length} image(s) uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (url) => {
    setForm((prev) => ({
      ...prev,
      imagesText: prev.imagesText.split("\n").map((s) => s.trim()).filter(Boolean).filter((u) => u !== url).join("\n"),
    }));
  };

  const load = useCallback(() => {
    api.get("/products").then(({ data }) => setProducts(data.products)).finally(() => setLoaded(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const [rowDrag, setRowDrag] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectGroup = (group) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = group.every((p) => next.has(p.id));
      group.forEach((p) => (allSelected ? next.delete(p.id) : next.add(p.id)));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const bulkSetStatus = async (status) => {
    const ids = [...selected];
    if (!ids.length) return;
    if (status === "live" && !window.confirm(`Put ${ids.length} product(s) on sale? Anyone on their waitlist will get a "it just dropped" email.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.put("/admin/products/bulk-status", { ids, status });
      const label = status === "live" ? "on sale" : "back to coming soon";
      toast.success(`${data.updated} product(s) set ${label}${data.unlocked ? ` · ${data.unlocked} drop email(s) sent` : ""}`);
      clearSelection();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const persistOrder = async (orderedIds) => {
    try {
      await api.put("/admin/products/reorder", { ids: orderedIds });
      toast.success("Order saved — live on the site");
    } catch {
      toast.error("Could not save order");
      load();
    }
  };

  const moveRow = (collection, from, to) => {
    const group = products.filter((p) => p.collection === collection);
    if (to < 0 || to >= group.length || from === to) return;
    const reordered = [...group];
    const [m] = reordered.splice(from, 1);
    reordered.splice(to, 0, m);
    let gi = 0;
    const newFlat = products.map((p) => (p.collection === collection ? reordered[gi++] : p));
    setProducts(newFlat);
    persistOrder(reordered.map((p) => p.id));
  };

  const toggleFeatured = async (p) => {
    const next = !p.featured;
    setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, featured: next } : x)));
    try {
      await api.put(`/admin/products/${p.id}`, {
        name: p.name, collection: p.collection, price: p.price, description: p.description,
        sizes: p.sizes, images: p.images, status: p.status, featured: next,
        size_inventory: p.size_inventory ?? {}, low_stock_threshold: p.low_stock_threshold ?? 5,
        allow_backorder: p.allow_backorder ?? false,
      });
      toast.success(next ? "Marked as a highlighted pick" : "Removed from picks");
    } catch {
      toast.error("Could not update");
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, featured: !next } : x)));
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const resetForm = () => { setForm(EMPTY); setEditingId(null); };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      collection: p.collection,
      price: p.price,
      description: p.description,
      sizesText: (p.sizes || []).join(", "),
      imagesText: (p.images || []).join("\n"),
      status: p.status,
      featured: p.featured,
      size_inventory: { ...(p.size_inventory || {}) },
      low_stock_threshold: p.low_stock_threshold ?? 5,
      allow_backorder: p.allow_backorder ?? false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cloneProduct = (p) => {
    setEditingId(null);
    setForm({
      name: `${p.name} (Copy)`,
      collection: p.collection,
      price: p.price,
      description: p.description,
      sizesText: (p.sizes || []).join(", "),
      imagesText: (p.images || []).join("\n"),
      status: "coming_soon",
      featured: false,
      size_inventory: {},
      low_stock_threshold: p.low_stock_threshold ?? 5,
      allow_backorder: p.allow_backorder ?? false,
    });
    toast.success("Duplicated — tweak the details and hit Create");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const size_inventory = {};
    sizeList.forEach((s) => {
      size_inventory[s] = parseInt(form.size_inventory?.[s], 10) || 0;
    });
    const payload = {
      name: form.name,
      collection: form.collection,
      price: parseFloat(form.price) || 0,
      description: form.description,
      sizes: sizeList,
      images: form.imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
      status: form.status,
      featured: form.featured,
      size_inventory,
      low_stock_threshold: parseInt(form.low_stock_threshold, 10) || 0,
      allow_backorder: !!form.allow_backorder,
    };
    try {
      if (editingId) {
        await api.put(`/admin/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const inputCls = "w-full bg-transparent border-2 border-[#F2EFEB] h-11 px-3 focus:outline-none focus:border-[#DFFF00] text-sm";

  return (
    <div data-testid="products-panel">
      <CollectionOrder />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
      {/* Form */}
      <form onSubmit={save} data-testid="product-form" className="border-2 border-[#F2EFEB] p-6 h-fit">
        <p className="overline text-[#DFFF00] mb-4">{editingId ? "Edit Product" : "New Product"}</p>
        <label className="overline block mb-1">Name</label>
        <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name-input" required />

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <label className="overline block mb-1">Collection</label>
            <select className={inputCls} value={form.collection} onChange={(e) => set("collection", e.target.value)} data-testid="product-collection-select">
              {COLLECTIONS.map((c) => <option key={c} value={c} className="bg-[#090909]">{c}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-1">Price ($)</label>
            <input type="number" step="0.01" className={inputCls} value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="product-price-input" required />
          </div>
        </div>

        <label className="overline block mb-1 mt-4">Description</label>
        <textarea className="w-full bg-transparent border-2 border-[#F2EFEB] p-3 focus:outline-none focus:border-[#DFFF00] text-sm min-h-[80px]" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="product-desc-input" />

        <label className="overline block mb-1 mt-4">Sizes (comma separated)</label>
        <input className={inputCls} value={form.sizesText} onChange={(e) => set("sizesText", e.target.value)} data-testid="product-sizes-input" />

        <label className="overline block mb-2 mt-4">Product Images</label>
        {images.length > 0 && (
          <>
            <p className="font-mono-ui text-[0.65rem] opacity-60 mb-2">Drag to reorder · first image is the cover</p>
            <div className="grid grid-cols-4 gap-2 mb-3" data-testid="product-image-previews">
              {images.map((url, idx) => (
                <div
                  key={url}
                  draggable
                  onDragStart={() => setDragIndex(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { reorderImages(dragIndex, idx); setDragIndex(null); }}
                  onDragEnd={() => setDragIndex(null)}
                  data-testid={`image-thumb-${idx}`}
                  className={`relative aspect-square border-2 overflow-hidden group cursor-grab active:cursor-grabbing transition-opacity ${dragIndex === idx ? "opacity-40 border-[#DFFF00]" : "border-[#F2EFEB]/50"}`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover grayscale pointer-events-none" />
                  <span className={`absolute bottom-0 left-0 px-1.5 py-0.5 text-[0.6rem] font-mono-ui ${idx === 0 ? "bg-[#DFFF00] text-[#090909]" : "bg-[#090909] text-[#F2EFEB]"}`}>
                    {idx === 0 ? "COVER" : idx + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeImage(url)}
                    data-testid="remove-image-btn"
                    className="absolute top-0 right-0 bg-[#FF2A00] text-[#090909] w-6 h-6 text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
        <label className={`flex items-center justify-center gap-2 h-12 border-2 border-dashed border-[#DFFF00] text-[#DFFF00] uppercase text-xs tracking-widest cursor-pointer hover:bg-[#DFFF00]/10 transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
          {uploading ? "Uploading..." : "↑ Upload images (jpg/png/webp)"}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            onChange={uploadFiles}
            data-testid="product-image-upload"
            className="hidden"
          />
        </label>
        <details className="mt-2">
          <summary className="overline cursor-pointer opacity-60 hover:opacity-100">or paste image URLs</summary>
          <textarea className="w-full bg-transparent border-2 border-[#F2EFEB] p-3 mt-2 focus:outline-none focus:border-[#DFFF00] text-xs min-h-[60px]" value={form.imagesText} onChange={(e) => set("imagesText", e.target.value)} data-testid="product-images-input" placeholder="One URL per line" />
        </details>

        <div className="grid grid-cols-2 gap-3 mt-4 items-end">
          <div>
            <label className="overline block mb-1">Status</label>
            <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="product-status-select">
              <option value="coming_soon" className="bg-[#090909]">coming_soon</option>
              <option value="live" className="bg-[#090909]">live</option>
            </select>
          </div>
          <label className="flex items-center gap-2 h-11 text-sm cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="product-featured-input" />
            Featured
          </label>
        </div>

        <div className="border-2 border-[#F2EFEB]/40 p-3 mt-4">
          <p className="overline text-[#DFFF00] mb-3">Inventory (per size)</p>
          {sizeList.length === 0 ? (
            <p className="font-mono-ui text-xs opacity-60">Add sizes above to set stock per size.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="size-inventory-grid">
              {sizeList.map((s) => (
                <div key={s}>
                  <label className="overline block mb-1">{s}</label>
                  <input
                    type="number"
                    min="0"
                    className={inputCls}
                    value={form.size_inventory?.[s] ?? 0}
                    onChange={(e) =>
                      set("size_inventory", { ...(form.size_inventory || {}), [s]: parseInt(e.target.value, 10) || 0 })
                    }
                    data-testid={`product-inventory-${s}`}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="mt-3">
            <label className="overline block mb-1">Low-stock at (per size)</label>
            <input type="number" min="0" className={inputCls} value={form.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} data-testid="product-lowstock-input" />
          </div>
          <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
            <input type="checkbox" checked={form.allow_backorder} onChange={(e) => set("allow_backorder", e.target.checked)} data-testid="product-backorder-input" />
            Allow backorder when sold out (keep buyable at 0)
          </label>
          <p className="font-mono-ui text-[0.65rem] opacity-60 mt-2">Customers see the selected size’s stock — “Only N left” at or below the threshold, and “Backorder” (if enabled) or “Sold out” at 0. A product is only fully Sold Out when every size hits 0. Stock auto-decreases on each paid order.</p>
        </div>

        <div className="flex gap-3 mt-6">
          <button type="submit" disabled={saving} data-testid="product-save-btn" className="flex-1 h-12 border-2 border-[#F2EFEB] bg-[#F2EFEB] text-[#090909] uppercase tracking-widest text-sm hover:bg-[#DFFF00] hover:border-[#DFFF00] transition-colors disabled:opacity-60">
            {saving ? "..." : editingId ? "Update →" : "Create →"}
          </button>
          {editingId && (
            <button type="button" onClick={resetForm} className="h-12 px-5 border-2 border-[#FF2A00] text-[#FF2A00] uppercase text-xs hover:bg-[#FF2A00] hover:text-[#090909] transition-colors">Cancel</button>
          )}
        </div>
      </form>

      {/* List */}
      <div data-testid="product-list">
        <p className="overline text-[#DFFF00] mb-1">{loaded ? `${products.length} products` : "loading..."}</p>
        <p className="font-mono-ui text-[0.65rem] opacity-60 mb-4">Tick products to launch a full drop at once · drag rows (or ▲▼) to set display order</p>

        {selected.size > 0 && (
          <div data-testid="bulk-action-bar" className="sticky top-0 z-20 flex items-center gap-3 flex-wrap bg-[#DFFF00] text-[#090909] px-4 py-3 mb-4 border-2 border-[#090909]">
            <span data-testid="bulk-selected-count" className="font-mono-ui text-sm uppercase tracking-widest">{selected.size} selected</span>
            <div className="flex-1" />
            <button onClick={() => bulkSetStatus("live")} disabled={bulkBusy} data-testid="bulk-set-live-btn" className="border-2 border-[#090909] bg-[#090909] text-[#DFFF00] px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#FF2A00] hover:border-[#FF2A00] hover:text-[#F2EFEB] transition-colors disabled:opacity-50">
              {bulkBusy ? "..." : "→ Set Live (on sale)"}
            </button>
            <button onClick={() => bulkSetStatus("coming_soon")} disabled={bulkBusy} data-testid="bulk-set-comingsoon-btn" className="border-2 border-[#090909] px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#090909] hover:text-[#DFFF00] transition-colors disabled:opacity-50">
              Set Coming Soon
            </button>
            <button onClick={clearSelection} data-testid="bulk-clear-btn" className="font-mono-ui text-xs uppercase underline underline-offset-2 hover:text-[#FF2A00]">Clear</button>
          </div>
        )}

        {!loaded ? (
          <p className="opacity-60 py-8 animate-pulse">Loading products...</p>
        ) : (
          COLLECTIONS.map((col) => {
            const group = products.filter((p) => p.collection === col);
            if (!group.length) return null;
            const allSelected = group.every((p) => selected.has(p.id));
            return (
              <div key={col} className="mb-8" data-testid={`product-group-${col}`}>
                <div className="flex items-center gap-3 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer" title="Select all in this collection">
                    <input type="checkbox" checked={allSelected} onChange={() => toggleSelectGroup(group)} data-testid={`select-all-${col}`} className="w-4 h-4 accent-[#DFFF00]" />
                    <h4 className="font-display text-2xl uppercase">{col}</h4>
                  </label>
                </div>
                <div className="space-y-2">
                  {group.map((p, idx) => (
                    <div
                      key={p.id}
                      draggable
                      onDragStart={() => setRowDrag({ col, idx })}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => { if (rowDrag?.col === col) moveRow(col, rowDrag.idx, idx); setRowDrag(null); }}
                      onDragEnd={() => setRowDrag(null)}
                      data-testid={`product-item-${p.slug}`}
                      className={`flex items-center gap-3 border-2 p-3 cursor-grab active:cursor-grabbing transition-opacity ${rowDrag?.col === col && rowDrag?.idx === idx ? "opacity-40 border-[#DFFF00]" : "border-[#F2EFEB]/40"}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(p.id)}
                        onChange={() => toggleSelect(p.id)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`select-${p.slug}`}
                        className="w-4 h-4 accent-[#DFFF00] shrink-0"
                      />
                      <span className="font-mono-ui text-xs opacity-50 select-none">⠿</span>
                      <span className="font-mono-ui text-sm text-[#DFFF00] w-6 text-center">{idx + 1}</span>
                      <div className="w-12 h-12 border border-[#F2EFEB]/40 overflow-hidden shrink-0">
                        {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover grayscale pointer-events-none" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm uppercase truncate">{p.name}</p>
                        {(() => {
                          const si = p.size_inventory || {};
                          const total = Object.values(si).reduce((n, v) => n + (Number(v) || 0), 0);
                          const thr = p.low_stock_threshold ?? 5;
                          const stockCls = total <= 0 ? "text-[#FF2A00]" : total <= thr ? "text-[#DFFF00]" : "";
                          const perSize = (p.sizes || []).map((s) => `${s}:${si[s] ?? 0}`).join(" ");
                          return (
                            <>
                              <p className="text-xs opacity-60">
                                ${formatPrice(p.price)} · <span className={p.status === "live" ? "text-[#DFFF00]" : "text-[#FF2A00]"}>{p.status === "live" ? "LIVE" : "coming soon"}</span> · {p.waitlist_count} waiting · <span className={stockCls}>{total} in stock</span>{total <= 0 && p.allow_backorder ? " (backorder)" : ""}
                              </p>
                              {perSize && <p className="text-[0.65rem] font-mono-ui opacity-40 truncate">{perSize}</p>}
                            </>
                          );
                        })()}
                      </div>
                      <div className="flex flex-col">
                        <button onClick={() => moveRow(col, idx, idx - 1)} disabled={idx === 0} data-testid={`up-${p.slug}`} className="text-xs px-2 leading-tight hover:text-[#DFFF00] disabled:opacity-20">▲</button>
                        <button onClick={() => moveRow(col, idx, idx + 1)} disabled={idx === group.length - 1} data-testid={`down-${p.slug}`} className="text-xs px-2 leading-tight hover:text-[#DFFF00] disabled:opacity-20">▼</button>
                      </div>
                      <button onClick={() => toggleFeatured(p)} data-testid={`feature-${p.slug}`} title="Highlighted pick" className={`text-sm px-2 py-1 border-2 transition-colors ${p.featured ? "bg-[#FF2A00] text-[#F2EFEB] border-[#FF2A00]" : "border-[#F2EFEB]/50 hover:border-[#FF2A00]"}`}>{p.featured ? "★" : "☆"}</button>
                      <button onClick={() => cloneProduct(p)} data-testid={`clone-${p.slug}`} title="Duplicate" className="text-xs border-2 border-[#F2EFEB] px-3 py-1 hover:bg-[#DFFF00] hover:text-[#090909] transition-colors">Clone</button>
                      <button onClick={() => startEdit(p)} data-testid={`edit-${p.slug}`} className="text-xs border-2 border-[#F2EFEB] px-3 py-1 hover:bg-[#DFFF00] hover:text-[#090909] transition-colors">Edit</button>
                      <button onClick={() => del(p.id)} data-testid={`delete-${p.slug}`} className="text-xs border-2 border-[#FF2A00] text-[#FF2A00] px-3 py-1 hover:bg-[#FF2A00] hover:text-[#090909] transition-colors">Del</button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
      </div>
    </div>
  );
}
